import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

const SEASONS = [
  { key: "PE", label: "Stagione PE" },
  { key: "AI", label: "Stagione AI" },
];

const DEFAULT_STATS = {
  totale: 0,
  totale_web: 0,
  totale_moda: 0,
  con_stock: 0,
  disattivati_con_stock: 0,
  con_stock_pubblicati: 0,
  con_stock_non_pubblicati: 0,
  con_stock_problema: 0,
  pubblicati: 0,
  non_pubblicati: 0,
  problema: 0,
  non_pubblicare: 0,
  solo_img: 0,
  solo_testo: 0,
};

const sortBrandsByField = (list, field) => {
  if (!Array.isArray(list)) {
    return [];
  }
  return [...list].sort((a, b) => {
    const diff = (b?.[field] ?? 0) - (a?.[field] ?? 0);
    if (diff !== 0) {
      return diff;
    }
    return (a?.brand_name || "").localeCompare(b?.brand_name || "");
  });
};

const PrioritaBrandPage = ({ token, utente, temaColori, mobile }) => {
  const { buttonClassesGreen, buttonClassesWhite, buttonClassesOrange } =
    stilePulsanti;

  const [season, setSeason] = useState("PE");
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingChanges, setPendingChanges] = useState({});
  const [editingBrand, setEditingBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orderDirty, setOrderDirty] = useState(false);
  const [touchedBrands, setTouchedBrands] = useState(() => new Set());
  const touchedBrandsRef = useRef(new Set());
  const [saveSuccessVisible, setSaveSuccessVisible] = useState(false);

  const updateTouchedBrands = useCallback((nextSet) => {
    touchedBrandsRef.current = nextSet;
    setTouchedBrands(nextSet);
    if (nextSet.size > 0) {
      setOrderDirty(true);
    }
  }, []);

  const markOrderDirty = useCallback(() => {
    setSaveSuccessVisible(false);
  }, []);

  useEffect(() => {
    if (!saveSuccessVisible) {
      return;
    }
    const timer = setTimeout(() => setSaveSuccessVisible(false), 3000);
    return () => clearTimeout(timer);
  }, [saveSuccessVisible]);

  const canManage = Boolean(
    utente?.permessiAPI?.["update:assegnazioniPubblicazione"]
  );

  useEffect(() => {
    if (!token || !canManage) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const fetchBrands = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${apiUrl}/moda/brand-settings`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (cancelled) {
          return;
        }
        const payload = response?.data;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        const prepared = list.map((item) => ({
          ...item,
          stats: {
            ...DEFAULT_STATS,
            ...(item.stats ?? {}),
          },
        }));
        setBrands(sortBrandsByField(prepared, "priority_pe"));
        updateTouchedBrands(new Set());
        setOrderDirty(false);
        setSaveSuccessVisible(false);
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error("Errore recupero brand settings:", fetchError);
        setError(
          fetchError?.response?.data?.error ??
            fetchError.message ??
            "Impossibile recuperare i brand"
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchBrands();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token, canManage]);

  const enqueueFieldChange = (brandCode, field, value) => {
    setPendingChanges((prev) => ({
      ...prev,
      [brandCode]: {
        ...(prev[brandCode] ?? {}),
        [field]: value,
      },
    }));
    setBrands((prev) =>
      prev.map((brand) =>
        brand.brand_code === brandCode
          ? {
              ...brand,
              [field]: value,
            }
          : brand
      )
    );
  };

  const saveBrandChanges = async (brandCode, overrides = {}) => {
    if (!token) {
      return;
    }
    const changes = {
      ...(pendingChanges[brandCode] ?? {}),
      ...overrides,
    };
    if (Object.keys(changes).length === 0) {
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/moda/brand-settings/${brandCode}`, changes, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[brandCode];
        return next;
      });
    } catch (saveError) {
      console.error("Errore salvataggio brand settings:", saveError);
      setError(
        saveError?.response?.data?.error ??
          saveError.message ??
          "Errore durante il salvataggio"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSavePriorityOrder = async () => {
    if (!token) {
      return;
    }
    const field = season === "PE" ? "priority_pe" : "priority_ai";
    const total = sortedBrands.length;

    const payload = [];
    const updatedValues = new Map();

    sortedBrands.forEach((brand, index) => {
      const value = index === total - 1 ? 0 : total - index;
      updatedValues.set(brand.brand_code, value);
      payload.push({
        brand_code: brand.brand_code,
        season,
        priority: value,
      });
    });

    setSaving(true);
    try {
      await axios.put(`${apiUrl}/moda/brand-settings/priorities`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBrands((prev) =>
        prev.map((brand) =>
          updatedValues.has(brand.brand_code)
            ? {
                ...brand,
                [field]: updatedValues.get(brand.brand_code),
              }
            : brand
        )
      );

      updateTouchedBrands(new Set());
      setOrderDirty(false);
      setSaveSuccessVisible(true);
    } catch (saveError) {
      console.error("Errore salvataggio priorità:", saveError);
      setError(
        saveError?.response?.data?.error ??
          saveError.message ??
          "Errore durante il salvataggio delle priorità"
      );
    } finally {
      setSaving(false);
    }
  };

  const openModal = (brand) => {
    setEditingBrand({ ...brand });
  };

  const handleModalChange = (field, value) => {
    setEditingBrand((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveModalChanges = async () => {
    if (!editingBrand) {
      return;
    }
    const {
      brand_code,
      priority_pe,
      priority_ai,
      permesso_sito,
      permesso_mp,
      permesso_estero,
      inventory_sharing,
      dropshipping,
      note,
    } = editingBrand;

    await saveBrandChanges(brand_code, {
      priority_pe,
      priority_ai,
      permesso_sito,
      permesso_mp,
      permesso_estero,
      inventory_sharing,
      dropshipping,
      note,
    });
    setEditingBrand(null);
  };

  const activeSeasonField = season === "PE" ? "priority_pe" : "priority_ai";

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    setBrands((prev) => {
      const sorted = sortBrandsByField(prev, activeSeasonField);
      const unchanged =
        sorted.length === prev.length &&
        sorted.every((item, index) => item === prev[index]);
      return unchanged ? prev : sorted;
    });
  }, [activeSeasonField]);

  const sortedBrands = useMemo(() => brands, [brands]);

  const applyTouchedPriorities = useCallback(
    (list, touchedSet) => {
      if (!touchedSet || touchedSet.size === 0) {
        return list;
      }
      const total = list.length;
      let changed = false;
      const updated = list.map((brand, index) => {
        if (!touchedSet.has(brand.brand_code)) {
          return brand;
        }
        const derivedValue = index === total - 1 ? 0 : total - index;
        if (brand[activeSeasonField] === derivedValue) {
          return brand;
        }
        changed = true;
        return {
          ...brand,
          [activeSeasonField]: derivedValue,
        };
      });
      return changed ? updated : list;
    },
    [activeSeasonField]
  );

  const buildTouchedSet = (brandCode) => {
    const next = new Set(touchedBrandsRef.current);
    next.add(brandCode);
    return next;
  };

  const commitOrderChange = (computeNext) => {
    let outcome = null;
    setBrands((items) => {
      const next = computeNext(items);
      if (!next) {
        return items;
      }
      outcome = next;
      return next.list;
    });
    if (outcome) {
      updateTouchedBrands(outcome.touched);
      markOrderDirty();
    }
  };

  const moveBrandToTop = (brandCode) => {
    commitOrderChange((items) => {
      const index = items.findIndex((item) => item.brand_code === brandCode);
      if (index <= 0) {
        return null;
      }
      const touched = buildTouchedSet(brandCode);
      const reorderedList = [
        items[index],
        ...items.slice(0, index),
        ...items.slice(index + 1),
      ];
      return {
        list: applyTouchedPriorities(reorderedList, touched),
        touched,
      };
    });
  };

  const moveBrandToBottom = (brandCode) => {
    commitOrderChange((items) => {
      const index = items.findIndex((item) => item.brand_code === brandCode);
      if (index === -1 || index === items.length - 1) {
        return null;
      }
      const touched = buildTouchedSet(brandCode);
      const reorderedList = [
        ...items.slice(0, index),
        ...items.slice(index + 1),
        items[index],
      ];
      return {
        list: applyTouchedPriorities(reorderedList, touched),
        touched,
      };
    });
  };

  const moveBrandByOffset = (brandCode, offset) => {
    commitOrderChange((items) => {
      const index = items.findIndex((item) => item.brand_code === brandCode);
      const targetIndex = index + offset;
      if (
        index === -1 ||
        targetIndex < 0 ||
        targetIndex >= items.length ||
        index === targetIndex
      ) {
        return null;
      }
      const touched = buildTouchedSet(brandCode);
      const reorderedList = arrayMove(items, index, targetIndex);
      return {
        list: applyTouchedPriorities(reorderedList, touched),
        touched,
      };
    });
  };

  const moveBrandUp = (brandCode) => moveBrandByOffset(brandCode, -1);
  const moveBrandDown = (brandCode) => moveBrandByOffset(brandCode, 1);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    commitOrderChange((items) => {
      const oldIndex = items.findIndex((item) => item.brand_code === active.id);
      const newIndex = items.findIndex((item) => item.brand_code === over.id);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return null;
      }
      const touched = buildTouchedSet(active.id);
      const reorderedList = arrayMove(items, oldIndex, newIndex);
      return {
        list: applyTouchedPriorities(reorderedList, touched),
        touched,
      };
    });
  };

  if (!canManage) {
    return (
      <div className={`${temaColori} pa3`}>
        <div className="sfondoBiancoTema pa4 br3 shadow-4">
          <p className="ma0">
            Non hai i permessi per visualizzare questa pagina.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${temaColori} pa2 pa3-ns min-vh-100`}>
      <div className="sfondoBiancoTema br3 shadow-4 pa3">
        <div className="flex justify-between items-center flex-wrap">
          <div>
            <h2 className="ma0">Priorità pubblicazione brand</h2>
            <p className="ma0 gray f6">
              Assegna priorità e permessi operativi ai brand.
            </p>
          </div>
          <div className="mt3 mt0-ns flex items-center">
            {saveSuccessVisible && (
              <div className="mr2 flex items-center dark-green f6">
                <span className="material-icons f5 mr1">check_circle</span>
                <span>Ordine salvato</span>
              </div>
            )}
            <button
              type="button"
              className={`${
                orderDirty ? buttonClassesOrange : buttonClassesWhite
              } mr3`}
              disabled={saving}
              onClick={handleSavePriorityOrder}
            >
              Salva priorità {season}
            </button>
            <div className="mh2 h1 w1 br bw1 b--black-10"></div>
            {SEASONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`${
                  season === opt.key ? buttonClassesGreen : buttonClassesWhite
                } mr2`}
                onClick={() => setSeason(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt3 pa3 br2 bg-washed-red dark-red">{error}</div>
        )}

        <div className="mt3 overflow-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedBrands.map((brand) => brand.brand_code)}
              strategy={verticalListSortingStrategy}
            >
              <table className="w-100 ba b--black-10 collapse">
                <thead>
                  <tr className="bg-near-white">
                    <th className="pa2 w2" rowSpan={2}></th>
                    <th className="tl pa2" rowSpan={2}>
                      Brand
                    </th>
                    <th className="tc pa2 w5" rowSpan={2}>
                      Cambia priorità
                    </th>
                    <th className="tc pa2 sfondoColChiaro" colSpan={5}>
                      Permessi
                    </th>
                    <th className="tc pa2 sfondoColAlt" colSpan={10}>
                      Stato articoli - metriche operative su stock web&gt;0
                    </th>
                    <th className="tl pa2" rowSpan={2}>
                      Azioni
                    </th>
                  </tr>
                  <tr className="bg-near-white">
                    <th className="tc pa2">Sito</th>
                    <th className="tc pa2">MP</th>
                    <th className="tc pa2">Estero</th>
                    <th className="tc pa2">I.S.</th>
                    <th className="tc pa2">Drop</th>
                    <th className="tl pa2">Tot. moda</th>
                    <th className="tl pa2">Tot. web</th>
                    <th className="tl pa2">Stock web&gt;0</th>
                    <th className="tl pa2">Pubbl.</th>
                    <th className="tl pa2">Non pubbl.</th>
                    <th className="tl pa2">Problema</th>
                    <th className="tl pa2">Da non fare</th>
                    <th className="tl pa2">Solo img</th>
                    <th className="tl pa2">Solo testo</th>
                    <th className="tl pa2">% compl. (probl)</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && sortedBrands.length === 0 && (
                    <tr>
                      <td className="pa3 tc" colSpan={19}>
                        Nessun brand disponibile.
                      </td>
                    </tr>
                  )}
                  {sortedBrands.map((brand) => (
                    <SortableBrandRow
                      key={brand.brand_code}
                      brand={brand}
                      buttonClassesWhite={buttonClassesWhite}
                      openModal={openModal}
                      moveBrandToTop={moveBrandToTop}
                      moveBrandUp={moveBrandUp}
                      moveBrandDown={moveBrandDown}
                      moveBrandToBottom={moveBrandToBottom}
                    />
                  ))}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {editingBrand && (
        <div className="fixed top-0 left-0 w-100 h-100 flex items-center justify-center bg-black-50">
          <div className="bg-white pa4 br3 shadow-4 w-90 w-60-l">
            <h3>Impostazioni brand {editingBrand.brand_code}</h3>
            <div className="flex flex-wrap">
              <label className="w-50 pa2">
                <span className="db f7 ttu gray">Priorità PE</span>
                <input
                  type="number"
                  className="input-reset ba b--black-20 br2 pa2 w-100"
                  value={editingBrand.priority_pe ?? 0}
                  onChange={(event) =>
                    handleModalChange("priority_pe", Number(event.target.value))
                  }
                />
              </label>
              <label className="w-50 pa2">
                <span className="db f7 ttu gray">Priorità AI</span>
                <input
                  type="number"
                  className="input-reset ba b--black-20 br2 pa2 w-100"
                  value={editingBrand.priority_ai ?? 0}
                  onChange={(event) =>
                    handleModalChange("priority_ai", Number(event.target.value))
                  }
                />
              </label>
              <label className="w-50 pa2">
                <span className="db f7 ttu gray">Permesso sito</span>
                <select
                  className="input-reset ba b--black-20 br2 pa2 w-100"
                  value={editingBrand.permesso_sito ? 1 : 0}
                  onChange={(event) =>
                    handleModalChange(
                      "permesso_sito",
                      Number(event.target.value)
                    )
                  }
                >
                  <option value={1}>Sì</option>
                  <option value={0}>No</option>
                </select>
              </label>
              <label className="w-50 pa2">
                <span className="db f7 ttu gray">Permesso marketplace</span>
                <select
                  className="input-reset ba b--black-20 br2 pa2 w-100"
                  value={editingBrand.permesso_mp ? 1 : 0}
                  onChange={(event) =>
                    handleModalChange("permesso_mp", Number(event.target.value))
                  }
                >
                  <option value={1}>Sì</option>
                  <option value={0}>No</option>
                </select>
              </label>
              <label className="w-50 pa2">
                <span className="db f7 ttu gray">Permesso estero</span>
                <select
                  className="input-reset ba b--black-20 br2 pa2 w-100"
                  value={editingBrand.permesso_estero ? 1 : 0}
                  onChange={(event) =>
                    handleModalChange(
                      "permesso_estero",
                      Number(event.target.value)
                    )
                  }
                >
                  <option value={1}>Sì</option>
                  <option value={0}>No</option>
                </select>
              </label>
              <label className="w-50 pa2">
                <span className="db f7 ttu gray">Inventory sharing</span>
                <select
                  className="input-reset ba b--black-20 br2 pa2 w-100"
                  value={editingBrand.inventory_sharing ? 1 : 0}
                  onChange={(event) =>
                    handleModalChange(
                      "inventory_sharing",
                      Number(event.target.value)
                    )
                  }
                >
                  <option value={1}>Sì</option>
                  <option value={0}>No</option>
                </select>
              </label>
              <label className="w-50 pa2">
                <span className="db f7 ttu gray">Dropshipping</span>
                <select
                  className="input-reset ba b--black-20 br2 pa2 w-100"
                  value={editingBrand.dropshipping ? 1 : 0}
                  onChange={(event) =>
                    handleModalChange(
                      "dropshipping",
                      Number(event.target.value)
                    )
                  }
                >
                  <option value={1}>Sì</option>
                  <option value={0}>No</option>
                </select>
              </label>
              <label className="w-100 pa2">
                <span className="db f7 ttu gray">Note</span>
                <textarea
                  className="input-reset ba b--black-20 br2 pa2 w-100"
                  rows={4}
                  value={editingBrand.note ?? ""}
                  onChange={(event) =>
                    handleModalChange("note", event.target.value)
                  }
                />
              </label>
            </div>
            <div className="flex justify-end mt3">
              <button
                type="button"
                className={`${buttonClassesWhite} mr2`}
                onClick={() => setEditingBrand(null)}
              >
                Annulla
              </button>
              <button
                type="button"
                className={buttonClassesGreen}
                onClick={saveModalChanges}
                disabled={saving}
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SortableBrandRow = ({
  brand,
  buttonClassesWhite,
  openModal,
  moveBrandToTop,
  moveBrandUp,
  moveBrandDown,
  moveBrandToBottom,
}) => {
  const stats = brand.stats ?? DEFAULT_STATS;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: brand.brand_code });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const completionPercent = (() => {
    const problemaStock =
      typeof stats.con_stock_problema === "number"
        ? stats.con_stock_problema
        : stats.problema ?? 0;
    const completati =
      (stats.pubblicati ?? 0) +
      (stats.non_pubblicare ?? 0) +
      problemaStock;
    const base = stats.con_stock ?? 0;
    if (base <= 0) {
      return { completion: 0, problema: problemaStock };
    }
    const completion = Math.round(
      Math.min(1, Math.max(0, completati / base)) * 100
    );
    const problemaPerc = Math.round(
      Math.min(1, Math.max(0, problemaStock / base)) * 100
    );
    return { completion, problema: problemaPerc };
  })();

  const background = (() => {
    const { completion, problema } = completionPercent;
    const safeCompletion = Math.min(100, Math.max(0, completion));
    const safeProblema = Math.min(safeCompletion, Math.max(0, problema));
    const greenEnd = Math.max(0, safeCompletion - safeProblema);
    const orangeEnd = safeCompletion;
    return `linear-gradient(90deg,
      rgba(19,119,82,0.25) 0%,
      rgba(19,119,82,0.25) ${greenEnd}%,
      rgba(255,165,0,0.35) ${greenEnd}%,
      rgba(255,165,0,0.35) ${orangeEnd}%,
      rgba(255,65,54,0.1) ${orangeEnd}%,
      rgba(255,65,54,0.1) 100%)`;
  })();

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="bb b--black-10"
      {...attributes}
    >
      <td className="pa2 w2">
        <button
          type="button"
          className="bn bg-transparent pointer flex items-center justify-center gray"
          {...attributes}
          {...listeners}
        >
          <span className="material-icons">drag_indicator</span>
        </button>
      </td>
      <td className="pa2">
        <div className="b">{brand.brand_name}</div>
        <div className="gray f7">COD: {brand.brand_code}</div>
      </td>
      <td className="pa2 w5">
        <div className="flex items-center justify-around">
          <button
            type="button"
            className="bn bg-transparent pointer flex items-center justify-center gray"
            onClick={() => moveBrandToTop(brand.brand_code)}
            title="Porta in cima"
          >
            <span className="material-icons">vertical_align_top</span>
          </button>
          <button
            type="button"
            className="bn bg-transparent pointer flex items-center justify-center gray"
            onClick={() => moveBrandUp(brand.brand_code)}
            title="Sposta su di uno"
          >
            <span className="material-icons">arrow_upward</span>
          </button>
          <button
            type="button"
            className="bn bg-transparent pointer flex items-center justify-center gray"
            onClick={() => moveBrandDown(brand.brand_code)}
            title="Sposta giù di uno"
          >
            <span className="material-icons">arrow_downward</span>
          </button>
          <button
            type="button"
            className="bn bg-transparent pointer flex items-center justify-center gray"
            onClick={() => moveBrandToBottom(brand.brand_code)}
            title="Porta in fondo"
          >
            <span className="material-icons">vertical_align_bottom</span>
          </button>
        </div>
      </td>
      {[
        { value: brand.permesso_sito, type: "default" },
        { value: brand.permesso_mp, type: "default" },
        { value: brand.permesso_estero, type: "default" },
        { value: brand.inventory_sharing, type: "slash" },
        { value: brand.dropshipping, type: "slash" },
      ].map((perm, index) => {
        const icon = perm.value
          ? "check_circle"
          : perm.type === "slash"
          ? "do_not_disturb_on"
          : "cancel";
        const colorClass = perm.value
          ? "dark-green"
          : perm.type === "slash"
          ? "light-silver"
          : "light-red";
        return (
          <td className="tc pa2" key={`${brand.brand_code}-perm-${index}`}>
            <span
              className={`material-icons ${colorClass}`}
              title={perm.value ? "Consentito" : "Non consentito"}
            >
              {icon}
            </span>
          </td>
        );
      })}
      <td className="pa2">{stats.totale_moda ?? 0}</td>
      <td className="pa2">{stats.totale_web ?? stats.totale ?? 0}</td>
      <td className="pa2 b">{stats.con_stock}</td>
      <td className="pa2 dark-green">
        {stats.pubblicati ? stats.pubblicati : ""}
      </td>
      <td className="pa2 dark-red">
        {stats.non_pubblicati ? stats.non_pubblicati : ""}
      </td>
      <td className="pa2 orange">
        {stats.con_stock_problema
          ? stats.con_stock_problema
          : stats.problema
          ? stats.problema
          : ""}
      </td>
      <td className="pa2 dark-green">
        {stats.non_pubblicare ? stats.non_pubblicare : ""}
      </td>
      <td className="pa2 orange">{stats.solo_img ? stats.solo_img : ""}</td>
      <td className="pa2 orange">{stats.solo_testo ? stats.solo_testo : ""}</td>
      <td className="pa2">
        <div className="ba b--black-10 br2 pa1 tc" style={{ background }}>
          {completionPercent.completion
            ? `${completionPercent.completion}% (${stats.con_stock_problema ?? stats.problema ?? 0})`
            : ""}
        </div>
      </td>
      <td className="pa2">
        <button
          type="button"
          className={`${buttonClassesWhite} mb2 flex items-center justify-center`}
          onClick={() => openModal(brand)}
          title="Modifica impostazioni"
        >
          <span className="material-icons">edit</span>
        </button>
      </td>
    </tr>
  );
};

export default PrioritaBrandPage;
